import React, {
  useState,
  useEffect,
  ChangeEvent,
  KeyboardEvent,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  MapPinLineIcon,
  HotelPropertyIcon,
  AirplaneIcon,
  PointIcon,
  CloseIcon,
  Spinner,
} from "@/components/icons";
import { Option, OptionGroup } from "@/types/Search";
import styles from "./AutoSuggest.module.scss";
import Skeleton from "@/components/layout/Skeleton/Skeleton";
import { useTranslations } from "next-intl";
import { loadDestination } from "@/utils/search/destination";
import { setSearchValues } from "@/redux/reducer/initSearchSlice";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/types/RootState";
import { useUserJourney } from "@/hooks/useUserJourney";

interface AutoSuggestProps {
  name: string;
  value?: Option | null;
  errors?: any;
  touched?: any;
  label?: string;
  errorStyle: boolean;
  loadOptions: (
    inputValue: string,
    signal?: AbortSignal
  ) => Promise<{ options: OptionGroup[]; hasMore: boolean }>;
  className?: string;
  placeholder: string;
  onOptionSelect: (option: Option | null) => void;
  onClick?: () => void;
  isBannerSearchBox?: boolean;
}

type InputRefType = {
  focus: () => void;
};

const AutoSuggest = forwardRef<InputRefType, AutoSuggestProps>((props, ref) => {
  const {
    name,
    value,
    errors,
    touched,
    label,
    loadOptions,
    className,
    placeholder,
    onOptionSelect,
    errorStyle,
    onClick,
    isBannerSearchBox = false,
    ...rest
  } = props;
  const [inputValue, setInputValue] = useState(value ? value.label : "");
  const [currentOptions, setCurrentOptions] = useState<OptionGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOptionSelected, setIsOptionSelected] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [hasFocused, setHasFocused] = useState(false);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState(false);
  const { trackEvent, getFullUrl } = useUserJourney("1231231211");
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const t = useTranslations();
  const router = useRouter();
  const searchState = useSelector((state: RootState) => state.hotelSearch);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  const memoizedLoadOptions = useCallback(
    (input: string, signal?: AbortSignal) => loadOptions(input, signal),
    [loadOptions]
  );

  useEffect(() => {
    if (!hasFocused || isOptionSelected) return;

    if (inputValue.trim().length < 3 || isOptionSelected) {
      setShowOptions(false);
      setIsOpen(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const newAbortController = new AbortController();
    abortControllerRef.current = newAbortController;
    const fetchOptions = async () => {
      setLoading(true);
      setShowOptions(false);
      setLoadingSpinner(true);

      try {
        const { options: fetchedOptionGroups } = await memoizedLoadOptions(
          inputValue,
          newAbortController.signal
        );

        if (newAbortController.signal.aborted) return;

        const sortedOptions = fetchedOptionGroups.sort(
          (a, b) =>
            sortOrder.indexOf(a.category) - sortOrder.indexOf(b.category)
        );
        setCurrentOptions(sortedOptions);
       
      } catch (error: any) {
        if (error.name !== "AbortError") {
          console.error("Failed to fetch options:", error);
        }
        console.error("Failed to load destination", error);
        trackEvent({
          page: router.pathname.replace("/", ""),
          url: getFullUrl(),
          action: "destination_select",
          api_payload: inputValue,
          api_response: error,
          api_error: true,
        });
      } finally {
        if (!newAbortController.signal.aborted) {
          setLoading(false);
        }
        setLoadingSpinner(false);
      }
    };

    fetchOptions();
    setShowOptions(true);

    return () => {
      newAbortController.abort();
    };
  }, [inputValue, isOptionSelected]);

  useEffect(() => {
    if (
      !loadingSpinner &&
      currentOptions.some((group) => group.options.length > 0)
    ) {
      setLoading(false);
      setHasFocused(true);
    }
  }, [loadingSpinner]);

  useEffect(() => {
    if (inputValue.trim() === "") {
      setIsOpen(false);
    } else if (currentOptions.some((group) => group.options.length > 0)) {
      setIsOpen(hasFocused && !loadingSpinner);
    } else {
      setIsOpen(false);
    }
  }, [inputValue, currentOptions, hasFocused]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    if (newValue !== inputValue) {
      setIsOptionSelected(false);
    }
    setIsOpen(false);
    if (newValue.trim() === "") {
      setIsOpen(false);
      setShowOptions(false);
      onOptionSelect(null);
    }
  };

  const handleOptionClick = (option: Option) => {
    setInputValue(option.label);
    setIsOptionSelected(true);
    setIsOpen(false);
    setShowOptions(false);
    setCurrentOptions([]);
    onOptionSelect(option);
     trackEvent({
          page: router.pathname.replace("/", ""),
          url: getFullUrl(),
          action: "destination_select",
          api_payload: option,
          api_response: currentOptions,
          api_error: false,
        });
    if (!isBannerSearchBox) {
      dispatch(
        setSearchValues({
          destination: {
            label: option.label,
            country: option.country,
            isProperty: option.isProperty,
            value: option.value,
            countryCode: option.countryCode,
            city: option.isProperty ? option.NAME_SMALL_CAPS : option.CITY_NAME,
            CITY_NAME: option.CITY_NAME ?? "",
            NAME_SMALL_CAPS: option.NAME_SMALL_CAPS,
          },
        })
      );
    }
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleClickOutside = (event: any) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  const sortOrder = [
    "city",
    "province_state",
    "airport",
    "point_of_interest",
    "property",
  ];

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchDestination = async () => {
      setLoadingLabel(true);
      try {
        if (searchState.destination.value.trim() !== "") {
          const res = await loadDestination(
            [searchState.destination.value],
            router.locale ?? "en"
          );
          if (
            res &&
            res.options?.length > 0 &&
            inputValue &&
            inputValue.trim() !== ""
          ) {
            const {
              label,
              country,
              value,
              countryCode,
              isProperty,
              CITY_NAME,
              NAME_SMALL_CAPS,
            } = res.options[0];
            setInputValue(label);
            dispatch(
              setSearchValues({
                destination: {
                  label: label,
                  country: country,
                  isProperty: isProperty,
                  value: value,
                  countryCode: countryCode,
                  city: isProperty ? CITY_NAME : NAME_SMALL_CAPS,
                  CITY_NAME: CITY_NAME,
                  NAME_SMALL_CAPS: NAME_SMALL_CAPS,
                },
              })
            );
          }
        }
      } catch (error) {
      } finally {
        setLoadingLabel(false);
      }
    };

    fetchDestination();
  }, [router.locale]);

  const getIconForCategory = (category: string) => {
    switch (category) {
      case "city":
      case "province_state":
        return <MapPinLineIcon />;
      case "airport":
        return <AirplaneIcon />;
      case "property":
        return <HotelPropertyIcon />;
      case "point_of_interest":
        return <PointIcon />;
      default:
        return null;
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (inputValue) {
      event.target.select();
    }
    setHasFocused(true);
    setIsOpen(false);
    setCurrentOptions([]);
  };

  const handleClear = () => {
    setInputValue("");
    setIsOptionSelected(false);
    setIsOpen(false);
    onOptionSelect(null);
    setHasFocused(false);
    setShowOptions(false);
    setCurrentOptions([]);
  };

  const replaceUnderscores = (name: string) => name.replace(/_/g, " ");

  const generateSkeletons = () => {
    return [1, 2].map((_, categoryIndex) => (
      <div
        key={`skeleton-category-${categoryIndex}`}
        className={`${styles.group}`}
      >
        <div className={`${styles.groupHeading} flex align-middle`}>
          <Skeleton
            width={30}
            height={20}
            className={styles.skeletonGroupHeading}
          />
        </div>
        {[1, 2].map((_, optionIndex) => (
          <div
            key={`skeleton-option-${categoryIndex}-${optionIndex}`}
            className={`${styles.AutoSuggestOption} flex p-2 justify-center items-center flex-col`}
          >
            <div className="flex flex-row justify-between w-full">
              <div className="flex flex-row gap-1">
                <Skeleton width={0} height={0} />
                <div className="flex flex-col gap-1">
                  <Skeleton width={30} height={20} />
                  <Skeleton width={214} height={20} />
                </div>
              </div>
              <Skeleton width={28} height={28} />
            </div>
          </div>
        ))}
      </div>
    ));
  };

  const groupedOptions = currentOptions.map(
    (group) =>
      group.options.length > 0 && (
        <div
          key={group.category}
          className={`${styles.group} border-2 border-[#efefef] shadow-lg`}
        >
          <div className={`${styles.groupHeading} flex align-middle`}>
            {loading ? (
              <Skeleton
                width={100}
                height={20}
                className={styles.skeletonGroupHeading}
              />
            ) : (
              <>{replaceUnderscores(t(group.category))}</>
            )}
          </div>
          {group.options.map((option, index) => (
            <div
              role="button"
              tabIndex={0}
              aria-label={`Select countries`}
              key={index}
              className={`${styles.AutoSuggestOption} flex px-4 py-2 justify-center items-center flex-col`}
              onClick={() => handleOptionClick(option)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  handleOptionClick(option);
                }
              }}
            >
              <div className="flex flex-row justify-between w-full items-start">
                <div className="flex flex-row space-x-3">
                  {group.category === "property" ? (
                    <img
                      src={
                        option.IMAGE_350
                          ? option.IMAGE_350
                          : "/images/hotel-placeholder.jpg"
                      }
                      className="w-[29px] h-[28px] rounded-full object-fill"
                    />
                  ) : (
                    <span className={styles.customIcon}>
                      {getIconForCategory(group.category)}
                    </span>
                  )}

                  <div className="flex flex-col gap-1">
                    <span className={styles.optionName}>{option.label}</span>
                    {option.country && (
                      <h5 className={styles.optionCountry}>{option.country}</h5>
                    )}
                  </div>
                </div>
                {option.countryCode && (
                  <span
                    className={`fi fi-${option.countryCode.toLowerCase()} ${
                      styles.optionCountryFlag
                    }`}
                  ></span>
                )}
              </div>
            </div>
          ))}
        </div>
      )
  );

  return (
    <div
      ref={containerRef}
      className={`${styles.AutoSuggest} flex flex-col items-end`}
    >
      {loadingLabel ? (
        <div className="flex !items-start !justify-start w-full">
          <Skeleton height={15} width={100} />
        </div>
      ) : (
        <>
          <div
            className={`${styles.AutoSuggestContainer} w-full !h-full !min-h-6`}
          >
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder}
              onClick={onClick}
              onFocus={handleFocus}
              ref={inputRef}
              className={`${styles.AutoSuggestInput} ${
                errorStyle ? styles.error : ""
              } w-full !h-full !min-h-6 max-c-md:placeholder:!text-sm placeholder:max-c-md:!leading-5`}
              {...rest}
            />
            {isOpen && (
              <>
                {loading ? (
                  <div
                    className={`${styles.AutoSuggestDropdown} z-100 flex flex-col absolute rounded-md`}
                  >
                    {generateSkeletons()}
                  </div>
                ) : (
                  <>
                    {showOptions &&
                    groupedOptions.length > 0 &&
                    isOpen &&
                    inputValue.trim() !== "" &&
                    inputValue.length >= 3 ? (
                      <div
                        className={`${styles.AutoSuggestDropdown} shadow-lg z-100 flex flex-col absolute rounded-md`}
                      >
                        {groupedOptions}
                      </div>
                    ) : (
                      <div
                        className={`${styles.AutoSuggestDropdown} shadow-lg z-100 flex-col absolute rounded-md`}
                      >
                        {showOptions &&
                          inputValue.trim() !== "" &&
                          isOpen &&
                          inputValue.length >= 3 && (
                            <div className={styles.noOptions}>
                              No options available
                            </div>
                          )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            {loadingSpinner && (
              <span className="absolute ltr:right-3 rtl:left-3 top-[28%] text-primary">
                {" "}
                <Spinner />
              </span>
            )}
            {inputValue && !loadingSpinner && (
              <button
                onClick={handleClear}
                className="absolute ltr:right-3 rtl:left-3 top-[38%]"
              >
                <CloseIcon size="sm" color="#676767" />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
});

export default AutoSuggest;
